import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Typography,
  IconButton, Checkbox, Stack, CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon, Lock as LockIcon } from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useInventoryContext } from '../contexts/InventoryContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import { updateEvent, getEvent, Event, Task } from '../services/eventsService';

interface Props {
  open: boolean;
  event: Event;
  onClose: () => void;
  onSaved: () => void;
}

const toDateInput = (d: Date | string | undefined): string => {
  if (!d) return '';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

/**
 * Edição leve para CO-EDITORES: só logística e tarefas.
 * NUNCA toca em guests/recipes/inventoryItems (listas privadas do dono),
 * evitando perda de dados. Faz updateEvent parcial.
 */
export const CoEditorEventDialog: React.FC<Props> = ({ open, event, onClose, onSaved }) => {
  const { theme } = useTheme();
  const { user, hostlyUser } = useAuth();
  const { items: myItems } = useInventoryContext();
  const { isPro } = useSubscription();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [location, setLocation] = useState('');
  const [outfit, setOutfit] = useState('');
  const [description, setDescription] = useState('');
  const [guestCount, setGuestCount] = useState('');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [newTask, setNewTask] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contributions, setContributions] = useState<NonNullable<Event['sharedInventoryItems']>>([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(event.title || '');
    setDate(toDateInput(event.date));
    setTime(event.time || '');
    setLocation(event.location || '');
    setOutfit(event.outfit || '');
    setDescription(event.description || '');
    setGuestCount(event.guestCount != null ? String(event.guestCount) : '');
    setTasks(event.tasks ? event.tasks.map(t => ({ ...t })) : []);
    setContributions(event.sharedInventoryItems ? event.sharedInventoryItems.map(c => ({ ...c })) : []);
    setError(null);
  }, [open, event]);

  const addTask = () => {
    const label = newTask.trim();
    if (!label) return;
    setTasks(prev => [...prev, { id: `t_${prev.length}_${label.slice(0, 4)}`, label, checked: false }]);
    setNewTask('');
  };

  const save = async () => {
    if (!title.trim()) { setError('O título é obrigatório'); return; }
    setSaving(true);
    setError(null);
    try {
      await updateEvent(event.id, {
        title: title.trim(),
        date,
        time,
        location: location.trim(),
        outfit: outfit.trim(),
        description: description.trim(),
        guestCount: guestCount ? parseInt(guestCount, 10) : 0,
        tasks,
        sharedInventoryItems: [
          ...(((await getEvent(event.id))?.sharedInventoryItems) || []).filter(c => c.ownerId !== user?.uid),
          ...contributions.filter(c => c.ownerId === user?.uid),
        ],
      });
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Editar evento (co-editor)</DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" sx={{ color: theme.colors.text.secondary, display: 'block', mb: 2 }}>
          Como co-editor você edita a logística e as tarefas. O acervo, receitas e convidados são gerenciados pelo dono.
        </Typography>
        <Stack spacing={2}>
          <TextField label="Título" value={title} onChange={e => setTitle(e.target.value)} size="small" fullWidth />
          <Stack direction="row" spacing={2}>
            <TextField label="Data" type="date" value={date} onChange={e => setDate(e.target.value)} size="small" fullWidth InputLabelProps={{ shrink: true }} />
            <TextField label="Hora" type="time" value={time} onChange={e => setTime(e.target.value)} size="small" fullWidth InputLabelProps={{ shrink: true }} />
          </Stack>
          <TextField label="Local" value={location} onChange={e => setLocation(e.target.value)} size="small" fullWidth />
          <TextField label="Quantidade de pessoas" value={guestCount} onChange={e => setGuestCount(e.target.value.replace(/\D/g, ''))} size="small" fullWidth inputProps={{ inputMode: 'numeric' }} />
          <TextField label="O que usar (dress code)" value={outfit} onChange={e => setOutfit(e.target.value)} size="small" fullWidth />
          <TextField label="Descrição" value={description} onChange={e => setDescription(e.target.value)} size="small" fullWidth multiline minRows={2} />

          <Box>
            <Typography variant="subtitle2" sx={{ color: theme.colors.text.primary, fontWeight: 700, mb: 0.5 }}>
              O que eu vou levar
            </Typography>
            <Typography variant="caption" sx={{ color: theme.colors.text.secondary, display: 'block', mb: 1 }}>
              {isPro
                ? 'Itens do seu acervo que você entra no evento.'
                : 'Entrar com itens do seu acervo é do plano Pro.'}
            </Typography>
            <Stack spacing={0.5} sx={{ mb: 1 }}>
              {contributions.filter(c => c.ownerId === user?.uid).map((c) => (
                <Box key={c.itemId} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ flex: 1, color: theme.colors.text.primary }}>
                    {c.itemName}{c.quantity && c.quantity > 1 ? ` (${c.quantity})` : ''}
                  </Typography>
                  <IconButton size="small" onClick={() => setContributions(prev => prev.filter(x => x.itemId !== c.itemId))}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              {contributions.filter(c => c.ownerId === user?.uid).length === 0 && (
                <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                  Você ainda não incluiu nada.
                </Typography>
              )}
            </Stack>
            {isPro ? (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setPickerOpen(true)}
                sx={{ textTransform: 'none', color: theme.colors.primary }}
              >
                Incluir item do meu acervo
              </Button>
            ) : (
              <Button
                size="small"
                startIcon={<LockIcon />}
                onClick={() => navigate('/pricing')}
                sx={{ textTransform: 'none', color: theme.colors.text.secondary }}
              >
                Disponível no Pro
              </Button>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ color: theme.colors.text.primary, fontWeight: 700, mb: 1 }}>Tarefas</Typography>
            <Stack spacing={0.5} sx={{ mb: 1 }}>
              {tasks.map((t, i) => (
                <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Checkbox
                    size="small"
                    checked={t.checked}
                    onChange={() => setTasks(prev => prev.map((x, j) => j === i ? { ...x, checked: !x.checked } : x))}
                  />
                  <TextField
                    value={t.label}
                    onChange={e => setTasks(prev => prev.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
                    size="small" variant="standard" fullWidth
                  />
                  <IconButton size="small" onClick={() => setTasks(prev => prev.filter((_, j) => j !== i))}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Stack>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                placeholder="Nova tarefa" value={newTask}
                onChange={e => setNewTask(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTask(); } }}
                size="small" fullWidth
              />
              <IconButton onClick={addTask} sx={{ backgroundColor: `${theme.colors.primary}18`, color: theme.colors.primary }}>
                <AddIcon />
              </IconButton>
            </Box>
          </Box>

          {error && <Typography variant="body2" sx={{ color: '#c62828' }}>{error}</Typography>}
        </Stack>
      </DialogContent>
      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Meu acervo</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={0.5}>
            {myItems.map((it) => {
              const already = contributions.some(c => c.itemId === it.id && c.ownerId === user?.uid);
              return (
                <Box
                  key={it.id}
                  onClick={() => {
                    if (already) {
                      setContributions(prev => prev.filter(c => !(c.itemId === it.id && c.ownerId === user?.uid)));
                      return;
                    }
                    setContributions(prev => [...prev, {
                      itemId: it.id,
                      itemName: it.itemName,
                      ownerId: user?.uid || '',
                      ownerName: `${hostlyUser?.name || ''} ${hostlyUser?.lastName || ''}`.trim() || 'Convidado',
                      quantity: 1,
                    }]);
                  }}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, p: 1, cursor: 'pointer',
                    borderRadius: theme.radii.medium,
                    border: `1px solid ${already ? theme.colors.primary : theme.colors.border}`,
                    backgroundColor: already ? `${theme.colors.primary}10` : 'transparent',
                  }}
                >
                  <Checkbox size="small" checked={already} sx={{ p: 0.5 }} />
                  <Typography variant="body2" sx={{ color: theme.colors.text.primary }}>{it.itemName}</Typography>
                </Box>
              );
            })}
            {myItems.length === 0 && (
              <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                Seu acervo está vazio.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)} sx={{ color: theme.colors.text.secondary }}>Concluir</Button>
        </DialogActions>
      </Dialog>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ color: theme.colors.text.secondary }}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={saving} sx={{ backgroundColor: theme.colors.primary }}>
          {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
